import React, {useEffect, useMemo, useState} from 'react'
import {User, UserRoles} from "../../types";
import {deleteUser, getUsers, reactivateUser} from "../../api/dataService";
import {Navigate} from "react-router-dom";
import StackedContentContainer from "../ContentContainer";
import {ColumnDef, Row} from "@tanstack/react-table";
import SearchableTable from "../SearchableTable";
import {Col, Button, Modal, Row as BRow, ButtonGroup, Form} from "react-bootstrap"
import CreateEmployee from "../CreateEmployee";
import DeleteConfirmationModal from "../DeleteConfirmationModal";
import {checkIsAllowed, isLoadingSpinner} from "../../util/Helpers";
import {useSelector} from "react-redux";
import {RootState} from "../../redux/reduxTypes";

const parentStyle = {
    padding: '2rem',
}

const getId = (u: Partial<User> | undefined | null): string | null => {
    if (!u) return null;
    const anyU = u as any;
    const id = anyU.id;
    if (typeof id === 'string' && id.length > 0) return id;
    if (typeof id === 'number' && Number.isFinite(id)) return String(id);
    return null;
};

function ManageEmployeesPage() {
    const [employeeList, setEmployeeList] = useState<User[]>([])
    const [isLoading, setIsLoading] = useState(true)

    const [showCreateModal, setShowCreateModal] = useState(false)

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedUserToDelete, setSelectedUserToDelete] = useState<User | null>(null);

    const [showReactivateModal, setShowReactivateModal] = useState(false);
    const [selectedUserToReactivate, setSelectedUserToReactivate] = useState<User | null>(null);

    const [employeeToUpdate, setEmployeeToUpdate] = useState<User | undefined>()

    const [hideInactive, setHideInactive] = useState(true);

    const user = useSelector((state: RootState) => state.user)

    useEffect(() => {
        Promise.all([getUsers(undefined)])
            .then(([userResponse]) => {
                if ('redirectTo' in userResponse) {
                    return (<Navigate to={userResponse.redirectTo}/>)
                }
                if (Array.isArray(userResponse)) {
                    setEmployeeList(userResponse)
                }
                setIsLoading(false);
            })
            .catch(error => {
                console.error("Error fetching data:", error);
                setIsLoading(false);
            });
    }, []);

    const displayedEmployees = useMemo(() => {
        if (!hideInactive) return employeeList;
        return employeeList.filter(e => e.is_active);
    }, [employeeList, hideInactive]);

    // Upsert by id (works even if CreateEmployee hands us a Partial<User> as long as it has an id)
    const upsertEmployee = (updated: Partial<User>) => {
        const updatedId = getId(updated);
        if (!updatedId) {
            console.warn("Upsert skipped: no id on updated object.", updated);
            return;
        }

        setEmployeeList(prev => {
            const idx = prev.findIndex(u => String((u as any).id) === updatedId);
            if (idx === -1) {
                // If we don't have it, insert (create path)
                return [updated as User, ...prev];
            }

            // Merge so we don't accidentally drop fields if updated is partial
            const merged = {...prev[idx], ...updated} as User;
            const copy = [...prev];
            copy[idx] = merged;
            return copy;
        });
    };

    const handleCloseCreate = () => {
        setShowCreateModal(false)
        setEmployeeToUpdate(undefined)
    }

    const handleShowCreate = () => setShowCreateModal(true)

    const handleOpenDetails = (row: Row<User>) => {
        setEmployeeToUpdate(row.original)
        setShowCreateModal(true)
    }

    const handleDeleteRow = (row: Row<User>) => {
        setSelectedUserToDelete(row.original);
        setShowDeleteModal(true);
    };

    const handleReactivateRow = (row: Row<User>) => {
        setSelectedUserToReactivate(row.original);
        setShowReactivateModal(true);
    };

    const handleConfirmDelete = () => {
        if (!selectedUserToDelete) return;

        deleteUser(String((selectedUserToDelete as any).id), selectedUserToDelete)
            .then((updatedUser: User) => {
                upsertEmployee(updatedUser);
                setShowDeleteModal(false);
                setSelectedUserToDelete(null);
            })
            .catch((e) => console.error("Error deleting/deactivating user:", e));
    };

    const handleConfirmReactivate = () => {
        if (!selectedUserToReactivate) return;

        reactivateUser(String((selectedUserToReactivate as any).id), selectedUserToReactivate)
            .then((updatedUser: User) => {
                upsertEmployee(updatedUser);
                setShowReactivateModal(false);
                setSelectedUserToReactivate(null);
            })
            .catch((e) => console.error("Error reactivating user:", e));
    };

    // accept Partial<User> because CreateEmployee props expect that,
    // will still immediately update if it contains an id.
    const handleCreate = (createdObject: Partial<User>) => {
        upsertEmployee(createdObject);
        setShowCreateModal(false)
    }

    const handleUpdate = (updatedObject: Partial<User>) => {
        upsertEmployee(updatedObject);
        setShowCreateModal(false)
    }

    const columns = useMemo<ColumnDef<User, any>[]>(
        () => [
            {
                accessorFn: row => `${row.first_name} ${row.last_name}`,
                id: 'full_name',
                header: 'Employee Name',
                enableColumnFilter: true
            },
            {
                accessorFn: row => row.mobile_number,
                id: 'Mobile_Number',
                header: 'Phone',
                enableColumnFilter: false
            },
            {
                accessorFn: row => row.email,
                id: 'email',
                header: 'Email Address',
                enableColumnFilter: false
            },
            {
                accessorFn: row => row.is_active,
                id: 'is_active',
                header: 'Active',
                enableColumnFilter: false
            },
            {
                id: 'actions',
                header: () => 'Actions',
                cell: ({row}) => {
                    const canManageEmployees = checkIsAllowed(user.groups, [
                        UserRoles.ownership,
                        UserRoles.management,
                        UserRoles.sales,
                        UserRoles.officeAdmin
                    ]);

                    const canDelete = checkIsAllowed(user.groups, [
                        UserRoles.ownership,
                        UserRoles.management,
                        UserRoles.sales
                    ]);

                    if (!canManageEmployees) return <></>;

                    return (
                        <div className="d-flex justify-content-center">
                            <ButtonGroup className="btn-group-sm">
                                {row.original.is_active ? (
                                    <>
                                        <Button className={'btn-secondary'} onClick={() => handleOpenDetails(row)}>
                                            Update
                                        </Button>
                                        {canDelete ? (
                                            <Button className={'btn-danger'} onClick={() => handleDeleteRow(row)}>
                                                Delete
                                            </Button>
                                        ) : null}
                                    </>
                                ) : (
                                    <Button className={'btn-success'} onClick={() => handleReactivateRow(row)}>
                                        Reactivate
                                    </Button>
                                )}
                            </ButtonGroup>
                        </div>
                    );
                }
            }
        ],
        [user.groups]
    )

    if (isLoading) {
        return (isLoadingSpinner());
    }

    return (
        <div style={parentStyle}>
            <StackedContentContainer title={"Employee Directory"}>
                <SearchableTable
                    columns={columns}
                    data={displayedEmployees}
                    globalSearch={true}
                    actionParam={
                        checkIsAllowed(user.groups, [UserRoles.ownership, UserRoles.management, UserRoles.sales, UserRoles.officeAdmin]) ?
                            (<BRow className="align-items-center">
                                <Col className="d-flex gap-3 align-items-center">
                                    <Button onClick={handleShowCreate}>Add Employee</Button>

                                    <Form.Check
                                        type="checkbox"
                                        id="hide-inactive-employees"
                                        label="Hide inactive employees"
                                        checked={hideInactive}
                                        onChange={(e) => setHideInactive(e.target.checked)}
                                    />
                                </Col>
                            </BRow>) : undefined
                    }
                />
            </StackedContentContainer>

            <Modal show={showCreateModal} onHide={handleCloseCreate} size={"lg"}>
                <Modal.Header closeButton>
                    <Modal.Title>{employeeToUpdate ? "Update Employee" : "Create Employee"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <CreateEmployee
                        closeModal={handleCloseCreate}
                        employeeToUpdate={employeeToUpdate}
                        onCreate={handleCreate}
                        onUpdate={handleUpdate}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseCreate}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            <DeleteConfirmationModal
                show={showDeleteModal}
                onHide={() => {
                    setShowDeleteModal(false);
                    setSelectedUserToDelete(null);
                }}
                onConfirm={handleConfirmDelete}
            />

            <Modal
                show={showReactivateModal}
                onHide={() => {
                    setShowReactivateModal(false);
                    setSelectedUserToReactivate(null);
                }}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Reactivate Employee</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to reactivate{" "}
                    <strong>
                        {selectedUserToReactivate
                            ? `${selectedUserToReactivate.first_name} ${selectedUserToReactivate.last_name}`
                            : "this employee"}
                    </strong>
                    ?
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            setShowReactivateModal(false);
                            setSelectedUserToReactivate(null);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button variant="success" onClick={handleConfirmReactivate}>
                        Confirm Reactivate
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}

export default ManageEmployeesPage
